import { useRequest } from 'features/request/useRequest';
import type { PriceListRatioCoefficientRowData, ProjectData } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import type { AgGridReact } from 'ag-grid-react';
import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { getPriceListWhereVariables } from '../getWhereVariables';

import { priceListRatio } from './factorsQueries';

interface IUsePriceListFactors {
    contractId: ProjectData['contractId'];
    filterValues: Record<string, string[]>;
    gridRef: RefObject<AgGridReact<PriceListRatioCoefficientRowData>>;
}

export const usePriceListFactors = ({ contractId, filterValues, gridRef }: IUsePriceListFactors) => {
    const variables = useMemo(
        () => ({
            contractId,
            where: getPriceListWhereVariables(filterValues),
            take: 50,
        }),
        [contractId, filterValues],
    );

    const [priceListRatioData, , , makeRequestPriceListRatio] = useRequest({
        requestId: 'priceListRatio',
        query: priceListRatio,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'priceListRatio',
        requestFn: makeRequestPriceListRatio,
        requestVariables: variables,
    });

    useEffect(() => {
        if (priceListRatioData?.uerPriceListRatioRows?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                priceListRatioData.uerPriceListRatioRows.items,
                priceListRatioData.uerPriceListRatioRows.totalCount,
            );
        }
    }, [
        getRowParams,
        priceListRatioData?.uerPriceListRatioRows?.items,
        priceListRatioData?.uerPriceListRatioRows?.totalCount,
    ]);

    const refreshData = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.refreshInfiniteCache();
        }
    }, [gridRef]);

    const totalCount = useMemo(
        () => priceListRatioData?.uerPriceListRatioRows?.totalCount ?? 0,
        [priceListRatioData?.uerPriceListRatioRows?.totalCount],
    );

    const fetchedCount = useMemo<number>(() => {
        if (!getRowParams?.current?.endRow || !totalCount) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalCount) {
            return totalCount;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalCount, getRowParams?.current?.endRow]);

    return useMemo(
        () => ({
            datasource: dataSource,
            totalCount,
            fetchedCount,
            refreshData,
        }),
        [dataSource, totalCount, fetchedCount, refreshData],
    );
};
